<script lang="ts">
  /**
   * Слой масок (blur/пикселизация).
   *
   * Рисует canvas размером с исходное изображение и поверх накладывает
   * честно обработанные (applyMask) тайлы для каждой видимой маски.
   * Без этого слоя маски выглядят как чёрные прямоугольники.
   */
  import { editor } from '../../store/editor.svelte';
  import { renderMaskedRegion, type MaskRegion } from '../../rendering/raster';
  import type { BlurShape } from '../../domain/types';

  interface Props {
    /** src исходного изображения. */
    src: string;
    /** Ширина/высота исходного изображения в пикселях. */
    width: number;
    height: number;
    /** Экранный transform (как у <img>): translate + scale. */
    tx: number;
    ty: number;
    scale: number;
  }
  let { src, width, height, tx, ty, scale }: Props = $props();

  let canvasEl = $state<HTMLCanvasElement>();
  let sourceImg = $state<HTMLImageElement | null>(null);

  // Загрузить исходное изображение один раз (и при смене src).
  $effect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => (sourceImg = img);
    img.src = src;
  });

  // Перерисовать маски при изменении source/shapes.
  $effect(() => {
    if (!canvasEl || !sourceImg) return;
    
    // читаем параметры для реактивности
    for (const s of editor.shapesSorted) {
      void s.visible;
      void s.x;
      void s.y;
      if (s.type === 'blur') {
        void s.w;
        void s.h;
        void s.mode;
        void s.intensity;
      } else if (s.type === 'rect') {
        void s.w;
        void s.h;
        void s.fillEnabled;
        void s.fillMode;
        void s.intensity;
      }
    }

    const masks: { x: number; y: number; w: number; h: number; mode: 'blur' | 'pixelize'; intensity: number }[] = [];
    for (const s of editor.shapesSorted) {
      if (!s.visible) continue;
      if (s.type === 'blur' && s.w > 0 && s.h > 0) {
        masks.push({
          x: s.x,
          y: s.y,
          w: s.w,
          h: s.h,
          mode: s.mode,
          intensity: s.intensity,
        });
      } else if (s.type === 'rect' && s.fillEnabled && (s.fillMode === 'blur' || s.fillMode === 'pixelize') && s.w > 0 && s.h > 0) {
        masks.push({
          x: s.x,
          y: s.y,
          w: s.w,
          h: s.h,
          mode: s.fillMode as 'blur' | 'pixelize',
          intensity: s.intensity ?? 12,
        });
      }
    }

    const ctx = canvasEl.getContext('2d')!;
    ctx.clearRect(0, 0, width, height);
    for (const m of masks) {
      const region: MaskRegion = {
        x: m.x,
        y: m.y,
        w: m.w,
        h: m.h,
        mode: m.mode,
        intensity: m.intensity,
      };
      const tile = renderMaskedRegion(sourceImg, region);
      ctx.drawImage(tile, Math.floor(m.x), Math.floor(m.y));
    }
  });
</script>

<canvas
  bind:this={canvasEl}
  width={width}
  height={height}
  class="absolute top-0 left-0 max-w-none select-none pointer-events-none"
  style={`transform: translate(${tx}px, ${ty}px) scale(${scale}); transform-origin: 0 0;`}
></canvas>
